import { Controller } from "@hotwired/stimulus"
import homeRenovationIcon from 'images/home-renovation-icon.svg'
import homeDemolitionIcon from 'images/home-demolition-icon.svg'

/**
 * Command Palette Controller
 *
 * A Spotlight/Cmd+K style search and action palette.
 * Opens with Cmd+K (Mac) or Ctrl+K (Windows/Linux).
 */
export default class extends Controller {
  static targets = ["modal", "backdrop", "input", "results", "empty", "loading", "content"]
  static values = {
    open: { type: Boolean, default: false }
  }

  connect() {
    this.selectedIndex = -1
    this.items = []
    this.debounceTimer = null

    // Global keyboard listener
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleGlobalKeydown)

    // Listen for trigger button click
    this.triggerButton = document.getElementById('command-palette-trigger')
    if (this.triggerButton) {
      this.boundOpen = this.open.bind(this)
      this.triggerButton.addEventListener('click', this.boundOpen)
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleGlobalKeydown)
    if (this.triggerButton) {
      this.triggerButton.removeEventListener('click', this.boundOpen)
    }
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  handleGlobalKeydown(event) {
    // Cmd+K or Ctrl+K to open
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      this.toggle()
    }

    // Escape to close
    if (event.key === 'Escape' && this.openValue) {
      this.close()
    }
  }

  toggle() {
    this.openValue ? this.close() : this.open()
  }

  open() {
    this.openValue = true
    this.modalTarget.classList.remove('hidden')
    this.backdropTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')

    // Focus input after animation
    setTimeout(() => {
      this.inputTarget.focus()
      this.inputTarget.select()
    }, 50)

    // Show default results (actions + recent)
    this.showDefaultResults()
  }

  close() {
    this.openValue = false
    this.modalTarget.classList.add('hidden')
    this.backdropTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    this.inputTarget.value = ''
    this.selectedIndex = -1
  }

  closeOnBackdrop(event) {
    // Only close if clicking outside the content (on the backdrop area)
    if (!this.contentTarget.contains(event.target)) {
      this.close()
    }
  }

  // Called on input
  search() {
    const query = this.inputTarget.value.trim()

    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    if (query.length === 0) {
      this.showDefaultResults()
      return
    }

    if (query.length < 2) {
      return
    }

    this.showLoading()

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  performSearch(query) {
    fetch(`/projects/search?query=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" }
    })
      .then(response => response.json())
      .then(data => {
        this.hideLoading()
        if (data.length === 0) {
          this.showEmpty()
        } else {
          this.renderSearchResults(data)
        }
      })
      .catch(() => {
        this.hideLoading()
        this.showEmpty()
      })
  }

  showDefaultResults() {
    this.hideLoading()
    this.hideEmpty()

    const actions = [
      { type: 'action', icon: 'plus', label: 'Nouvelle adresse', url: '/projects/new' },
    ]

    this.items = actions
    this.selectedIndex = 0
    this.renderResults(actions, 'Actions')
  }

  renderSearchResults(projects) {
    this.hideEmpty()

    this.items = projects.map(p => ({
      type: 'project',
      id: p.id,
      label: p.full_address,
      kind: p.kind,
      url: `/projects/${p.id}`
    }))

    this.selectedIndex = 0

    const html = `
      <div class="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Adresses</div>
      ${this.items.map((item, index) => this.renderProjectItem(item, index)).join('')}
    `
    this.resultsTarget.innerHTML = html
    this.updateSelection()
  }

  renderResults(items, title) {
    const html = `
      <div class="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">${title}</div>
      ${items.map((item, index) => this.renderItem(item, index)).join('')}
    `
    this.resultsTarget.innerHTML = html
    this.updateSelection()
  }

  renderItem(item, index) {
    const icons = {
      plus: `<svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>`,
      search: `<svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>`,
    }

    return `
      <div class="command-palette-item flex items-center px-3 py-2 cursor-pointer hover:bg-indigo-50"
           data-index="${index}"
           data-url="${item.url}"
           data-action="click->command-palette#selectItem mouseenter->command-palette#hoverItem">
        <span class="flex-shrink-0 mr-3">${icons[item.icon] || ''}</span>
        <span class="text-sm text-gray-900">${item.label}</span>
      </div>
    `
  }

  renderProjectItem(item, index) {
    const iconUrl = item.kind === 'restoration' ? homeRenovationIcon : homeDemolitionIcon

    return `
      <div class="command-palette-item flex items-center px-3 py-2 cursor-pointer hover:bg-indigo-50"
           data-index="${index}"
           data-url="${item.url}"
           data-action="click->command-palette#selectItem mouseenter->command-palette#hoverItem">
        <img src="${iconUrl}" alt="" class="w-5 h-5 mr-3 flex-shrink-0">
        <span class="text-sm text-gray-900 truncate">${item.label}</span>
      </div>
    `
  }

  // Keyboard navigation
  navigate(event) {
    if (!this.openValue) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.items.length - 1)
        this.updateSelection()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection()
        break
      case 'Enter':
        event.preventDefault()
        this.confirmSelection()
        break
    }
  }

  updateSelection() {
    const items = this.resultsTarget.querySelectorAll('.command-palette-item')
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add('bg-indigo-50')
        item.scrollIntoView({ block: 'nearest' })
      } else {
        item.classList.remove('bg-indigo-50')
      }
    })
  }

  hoverItem(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.selectedIndex = index
    this.updateSelection()
  }

  selectItem(event) {
    const url = event.currentTarget.dataset.url
    if (url) {
      window.location.href = url
    }
  }

  confirmSelection() {
    if (this.selectedIndex >= 0 && this.items[this.selectedIndex]) {
      const item = this.items[this.selectedIndex]
      if (item.url) {
        window.location.href = item.url
      }
    }
  }

  showLoading() {
    this.loadingTarget.classList.remove('hidden')
    this.emptyTarget.classList.add('hidden')
  }

  hideLoading() {
    this.loadingTarget.classList.add('hidden')
  }

  showEmpty() {
    this.emptyTarget.classList.remove('hidden')
    this.resultsTarget.innerHTML = ''
  }

  hideEmpty() {
    this.emptyTarget.classList.add('hidden')
  }
}
